import React, { useContext, useEffect, useMemo, useState } from 'react';
import styles from './aiChat.module.scss';
import classnames from 'classnames';
import {
  AIConversation,
  AIConversationMessage,
  DjangoListResponse,
  MessageRole,
} from 'v2/models';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import AIChat from './AIChat';
import {
  AI_SEARCH_MESSAGES_PAGE_SIZE,
  AI_SEARCH_POLL_INTERVAL,
} from 'utils/constants';
import AIChatMessageInput from './AIChatMessageInput';
import { toast } from 'react-toastify';
import { Spinner } from 'react-bootstrap';
import {
  CurrentStateWaitingConversationContext,
  isAIMessageLimitReached,
  isAiSearchRunning,
} from 'v2/utils/AISearchUtils';
import { aiConversationKeys } from 'v2/queries';

interface AIMessageData {
  text: string;
  role: MessageRole;
  ai_conversation: number;
}

interface AIConversationPaneProps {
  aiSearch: AIConversation;
}

const AIConversationPane = ({ aiSearch }: AIConversationPaneProps) => {
  const [searchState, setSearchState] = useState(aiSearch.status);
  const queryClient = useQueryClient();

  const {
    data: conversationData,
    isSuccess: isConversationDataSuccess,
    refetch: refetchConversationData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ['AIConversationMessages', aiSearch.id],
    async ({ pageParam = 1 }) => {
      const response = await restFactory.get<
        DjangoListResponse<AIConversationMessage>
      >(`/dj/api/v1/ai_conversation_message/`, {
        page: pageParam,
        size: AI_SEARCH_MESSAGES_PAGE_SIZE,
        ai_conversation: aiSearch.id,
      });

      return {
        ...response,
        prev: pageParam,
        next: pageParam + 1,
      };
    },
    {
      cacheTime: 0,
      refetchOnMount: false,
      refetchInterval: () => {
        return isAiSearchRunning(aiSearch) ? AI_SEARCH_POLL_INTERVAL : false;
      },
      getNextPageParam: (lastPage) => {
        const hasMorePages =
          AI_SEARCH_MESSAGES_PAGE_SIZE * lastPage.prev < lastPage.count;
        return hasMorePages ? lastPage.next : undefined;
      },
    }
  );

  const { waitingConversation } = useContext(
    CurrentStateWaitingConversationContext
  );

  useEffect(() => {
    if (aiSearch.status !== searchState && !isAiSearchRunning(aiSearch)) {
      refetchConversationData();
    }
    setSearchState(aiSearch.status);
  }, [aiSearch]);

  const messages = useMemo(() => {
    if (!conversationData) return [];

    return conversationData.pages.flatMap((page) => page.results).reverse();
  }, [conversationData]);

  const { mutate: createMessage } = useMutation(
    async (data: AIMessageData) => {
      return await restFactory.post<AIConversationMessage>(
        `/dj/api/v1/ai_conversation_message/`,
        data
      );
    },
    {
      onSuccess: () => {
        refetchConversationData();
        queryClient.invalidateQueries('AIConversationList');
        queryClient.invalidateQueries(aiConversationKeys.detail(aiSearch.id));
      },
      onError: () => {
        toast.error('Error sending message.');
      },
    }
  );

  function handleSearch(searchText: string, ai_conversation: number) {
    createMessage({
      text: searchText,
      role: MessageRole.USER,
      ai_conversation,
    });
  }

  if (isLoading || !isConversationDataSuccess) {
    return (
      <div className={classnames(styles.customSpinner)}>
        <Spinner animation="border" />
      </div>
    );
  }

  const isLimitReached = isAIMessageLimitReached(messages);

  const showInput =
    !isLimitReached &&
    (waitingConversation === undefined || waitingConversation === aiSearch.id);

  return (
    <div className={classnames(styles.conversationContainer)}>
      <AIChat
        messages={messages}
        aiConversation={aiSearch}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
      />
      {showInput && (
        <AIChatMessageInput onSearch={handleSearch} aiConversation={aiSearch} />
      )}
    </div>
  );
};

export default AIConversationPane;
